import { ENV_NAME, getEnvironment } from '@heidi/api/config';
import { isMac } from '@heidi/runtime/platform';
import { getDefaultStore } from 'jotai';
import { atomWithStorage, createJSONStorage } from 'jotai/utils';
import type { KeyboardLayout } from './useKeyboardLayout';

const createSsrSafeStorage = <T>() =>
  createJSONStorage<T>(() => {
    if (typeof window === 'undefined') {
      return {
        getItem: () => null,
        setItem: () => undefined,
        removeItem: () => undefined,
      };
    }
    return window.localStorage;
  });

/** `null` = use the real OS platform (`@/hooks/platform`). Ignored in production. */
export type DictateOnboardingDevPlatformOverride = 'mac' | 'windows' | null;

/** `null` = auto-detect via `useKeyboardLayout`. Ignored in production. */
export type DictateOnboardingDevLayoutOverride = KeyboardLayout | null;

export const dictateOnboardingDevPlatformOverrideAtom =
  atomWithStorage<DictateOnboardingDevPlatformOverride>(
    'heidi.devTools.dictateOnboarding.platformOverride',
    null,
    createSsrSafeStorage<DictateOnboardingDevPlatformOverride>(),
    { getOnInit: true }
  );

dictateOnboardingDevPlatformOverrideAtom.debugLabel =
  'dictateOnboardingDevPlatformOverrideAtom';

export const dictateOnboardingDevLayoutOverrideAtom =
  atomWithStorage<DictateOnboardingDevLayoutOverride>(
    'heidi.devTools.dictateOnboarding.layoutOverride',
    null,
    createSsrSafeStorage<DictateOnboardingDevLayoutOverride>(),
    { getOnInit: true }
  );

dictateOnboardingDevLayoutOverrideAtom.debugLabel =
  'dictateOnboardingDevLayoutOverrideAtom';

export const isDictateOnboardingDevOverridesEnabled = (): boolean =>
  getEnvironment() !== ENV_NAME.PROD;

export const getDictateOnboardingDevPlatformOverride =
  (): DictateOnboardingDevPlatformOverride => {
    if (!isDictateOnboardingDevOverridesEnabled()) {
      return null;
    }
    return getDefaultStore().get(dictateOnboardingDevPlatformOverrideAtom);
  };

export const getDictateOnboardingDevLayoutOverride =
  (): DictateOnboardingDevLayoutOverride => {
    if (!isDictateOnboardingDevOverridesEnabled()) {
      return null;
    }
    return getDefaultStore().get(dictateOnboardingDevLayoutOverrideAtom);
  };

/** macOS vs Windows for dictate onboarding UI (keyboard row, padding, copy). */
export const getDictateOnboardingIsMac = (): boolean => {
  const override = getDictateOnboardingDevPlatformOverride();
  if (override === 'mac') return true;
  if (override === 'windows') return false;
  return isMac;
};
